import {
  BlendedRateEstimateSource,
  BlendedRateIssuerMatchType,
  ProjectedRateDto,
} from '@zorro/clients';
import { isDefined, stateNameByCode } from '@zorro/common-utils';
import { formatPercentage } from '@zorro/shared/formatters';

export const PROJECTED_RATE_LABEL = 'Projected';

/** Requested and approved filings share one sentence; only this word differs. */
const CARRIER_RATE_WORD: Partial<Record<BlendedRateEstimateSource, string>> = {
  [BlendedRateEstimateSource.CARRIER_SPECIFIC_REQUESTED]: 'requested',
  [BlendedRateEstimateSource.CARRIER_SPECIFIC_APPROVED]: 'approved',
};

/** -1 → "-1.0pp" */
const formatAdjustmentPp = (adjustmentPp: number): string =>
  `${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  }).format(adjustmentPp)}pp`;

const stateName = (stateCode: string | null | undefined): string =>
  (stateCode && stateNameByCode[stateCode]) || stateCode || 'the state';

export function getProjectedRateTooltipLabel(
  {
    year,
    rateChangePct,
    estimateSource,
    issuerMatchType,
    adjustmentPp,
  }: ProjectedRateDto,
  stateCode: string | null | undefined
): string {
  const direction = rateChangePct < 0 ? 'decrease' : 'increase';
  // `signDisplay` defaults to 'never', which would strip the minus off a
  // decrease; the copy spells the sign out either way.
  const pctView = formatPercentage(rateChangePct / 100, {
    maximumFractionDigits: 2,
    signDisplay: 'exceptZero',
  });
  const lead = `Projected ${pctView} ${direction}.`;

  if (estimateSource === BlendedRateEstimateSource.STATE_MEDIAN) {
    return `${lead} This carrier has not published a ${year} rate yet, so we applied the median ${direction} for ${stateName(stateCode)}.`;
  }

  const rateWord = estimateSource && CARRIER_RATE_WORD[estimateSource];
  if (!rateWord) {
    return lead;
  }

  const basis =
    issuerMatchType === BlendedRateIssuerMatchType.PARENT_ISSUER
      ? `the ${rateWord} ${year} rate filed by a related company of this carrier`
      : `this carrier's ${rateWord} ${year} rate`;
  const adjustment =
    estimateSource === BlendedRateEstimateSource.CARRIER_SPECIFIC_REQUESTED &&
    isDefined(adjustmentPp)
      ? `, adjusted by ${formatAdjustmentPp(adjustmentPp)} for expected regulator approval`
      : '';

  return `${lead} Based on ${basis}${adjustment}.`;
}
